// Email service for sending installation questionnaire data
// Using existing Mandrill (Mailchimp) setup from the contact form
export interface InstallationFormData {
  // Order information
  orderNumber?: string;
  customerName: string;
  customerEmail: string;
  productName: string;
  
  // Questionnaire responses
  hasCentralHVAC: boolean;
  iaqInterests: string;
  hasExistingEquipment: boolean;
  existingEquipmentDetails?: string;
  hasPreferredContractor: boolean;
  contractorContact?: string;
}

export interface PostPurchaseEmailData {
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  productName: string;
}

export class EmailService {
  private readonly mandrillApiKey = 'md-0hbkGwKEhBY5Z-5Q_ELcQA'; // Using existing key from contact form
  private readonly mandrillEndpoint = 'https://mandrillapp.com/api/1.0/messages/send.json';

  async sendPostPurchaseQuestionnaire(data: PostPurchaseEmailData): Promise<boolean> {
    try {
      const questionnaireUrl = `${window.location.origin}/order-confirmation?order=${data.orderNumber}&name=${encodeURIComponent(data.customerName)}&email=${encodeURIComponent(data.customerEmail)}&product=${encodeURIComponent(data.productName)}`;
      
      const emailContent = this.formatPostPurchaseEmail(data, questionnaireUrl);
      
      const mandrillPayload = {
        key: this.mandrillApiKey,
        message: {
          html: emailContent,
          text: emailContent.replace(/<[^>]*>/g, ''), // Strip HTML for text version
          subject: `Important: Complete Your Professional Installation Setup - Order ${data.orderNumber}`,
          from_email: 'noreply@haveniaq.com',
          from_name: 'HAVEN Installation Team',
          to: [
            {
              email: data.customerEmail,
              name: data.customerName,
              type: 'to'
            }
          ],
          important: true,
          track_opens: true,
          track_clicks: true,
          auto_text: true,
          auto_html: false,
          inline_css: true,
          url_strip_qs: false,
          preserve_recipients: false,
          view_content_link: false
        }
      };

      const response = await fetch(this.mandrillEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(mandrillPayload),
      });

      const result = await response.json();

      if (response.ok && result[0]?.status === 'sent') {
        return true;
      } else {
        throw new Error(result[0]?.reject_reason || 'Email delivery failed');
      }
    } catch (error) {
      console.error('Failed to send post-purchase questionnaire email:', error);
      return false;
    }
  }

  private formatPostPurchaseEmail(data: PostPurchaseEmailData, questionnaireUrl: string): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #00C4C1 0%, #FFE623 100%); padding: 30px; border-radius: 10px; margin-bottom: 30px; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 28px;">🎉 Thank You for Your Purchase!</h1>
          <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Order #${data.orderNumber}</p>
        </div>

        <div style="background: #f8f9fa; padding: 25px; border-radius: 8px; margin-bottom: 25px;">
          <h2 style="color: #00C4C1; margin-top: 0; font-size: 20px;">Important Next Step for Professional Installation</h2>
          <p style="margin: 0; font-size: 16px; line-height: 1.6;">
            Hi ${data.customerName},<br><br>
            Thank you for purchasing the <strong>${data.productName}</strong>! To ensure your HAVEN system is installed correctly 
            and works perfectly with your existing HVAC system, we need some additional information about your home setup.
          </p>
        </div>

        <div style="background: white; padding: 25px; border: 1px solid #e0e0e0; border-radius: 8px; margin-bottom: 25px; text-align: center;">
          <h3 style="color: #00C4C1; margin-top: 0; font-size: 18px;">Complete Your Installation Setup</h3>
          <p style="margin-bottom: 20px; font-size: 14px; color: #666;">
            This quick 4-question form helps us match you with the right contractor and ensure proper installation.
          </p>
          
          <a href="${questionnaireUrl}" 
             style="display: inline-block; background: #00C4C1; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 16px;">
            Complete Installation Setup →
          </a>
          
          <p style="margin-top: 15px; font-size: 12px; color: #999;">
            Or copy this link: ${questionnaireUrl}
          </p>
        </div>

        <div style="background: #fff3e0; padding: 20px; border-radius: 8px; border-left: 4px solid #ff9800; margin-bottom: 25px;">
          <h4 style="margin: 0 0 10px 0; color: #e65100; font-size: 16px;">⏰ Time Sensitive</h4>
          <p style="margin: 0; font-size: 14px; color: #333;">
            Please complete this within <strong>48 hours</strong> to avoid delays in your installation scheduling. 
            Our team will contact you within 1 week with contractor options and installation coordination.
          </p>
        </div>

        <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; text-align: center;">
          <h4 style="margin: 0 0 10px 0; color: #2e7d32; font-size: 16px;">📋 What We'll Ask:</h4>
          <ul style="margin: 0; padding: 0; list-style: none; font-size: 14px; color: #333;">
            <li style="margin: 5px 0;">✓ Central HVAC system details</li>
            <li style="margin: 5px 0;">✓ Indoor air quality interests</li>
            <li style="margin: 5px 0;">✓ Existing equipment information</li>
            <li style="margin: 5px 0;">✓ Contractor preferences</li>
          </ul>
        </div>

        <div style="margin-top: 30px; padding: 15px; background: #f8f9fa; border-radius: 5px; text-align: center; font-size: 12px; color: #666;">
          <p style="margin: 0;">Need help? Reply to this email or contact us at support@haveniaq.com</p>
          <p style="margin: 5px 0 0 0;">HAVEN Installation Team</p>
        </div>
      </div>
    `;
  }

  async sendInstallationForm(formData: InstallationFormData): Promise<boolean> {
    try {
      const emailContent = this.formatInstallationEmail(formData);
      
      const mandrillPayload = {
        key: this.mandrillApiKey,
        message: {
          html: emailContent,
          text: emailContent.replace(/<[^>]*>/g, ''), // Strip HTML for text version
          subject: `Professional Installation Details - Order ${formData.orderNumber || 'N/A'}`,
          from_email: 'noreply@haveniaq.com',
          from_name: 'HAVEN Store Installation',
          to: [
            {
              email: 'support@haveniaq.com',
              name: 'HAVEN Support Team',
              type: 'to'
            }
          ],
          headers: {
            'Reply-To': formData.customerEmail
          },
          important: true,
          track_opens: true,
          track_clicks: true,
          auto_text: true,
          auto_html: false,
          inline_css: true,
          url_strip_qs: false,
          preserve_recipients: false,
          view_content_link: false
        }
      };

      const response = await fetch(this.mandrillEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(mandrillPayload),
      });

      const result = await response.json();

      if (response.ok && result[0]?.status === 'sent') {
        return true;
      } else {
        throw new Error(result[0]?.reject_reason || 'Email delivery failed');
      }
    } catch (error) {
      console.error('Failed to send installation form email:', error);
      
      // Fallback to mailto as backup
      this.fallbackToMailto(formData);
      return false;
    }
  }

  private formatInstallationEmail(formData: InstallationFormData): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #00695C 0%, #FFE529 100%); padding: 30px; border-radius: 10px; margin-bottom: 30px;">
          <h1 style="color: white; margin: 0; font-size: 28px; text-align: center;">Professional Installation Questionnaire</h1>
          <p style="color: white; margin: 10px 0 0 0; text-align: center; font-size: 16px;">New Customer Submission</p>
        </div>

        <div style="background: #f8f9fa; padding: 25px; border-radius: 8px; margin-bottom: 25px;">
          <h2 style="color: #00695C; margin-top: 0; font-size: 20px; border-bottom: 2px solid #00695C; padding-bottom: 10px;">Order Information</h2>
          <p style="margin: 8px 0;"><strong>Order Number:</strong> ${formData.orderNumber || 'Not provided'}</p>
          <p style="margin: 8px 0;"><strong>Customer Name:</strong> ${formData.customerName}</p>
          <p style="margin: 8px 0;"><strong>Customer Email:</strong> ${formData.customerEmail}</p>
          <p style="margin: 8px 0;"><strong>Product:</strong> ${formData.productName}</p>
        </div>

        <div style="background: white; padding: 25px; border: 1px solid #e0e0e0; border-radius: 8px; margin-bottom: 25px;">
          <h2 style="color: #00695C; margin-top: 0; font-size: 20px; border-bottom: 2px solid #00695C; padding-bottom: 10px;">Installation Requirements</h2>
          
          <div style="margin-bottom: 20px;">
            <h3 style="color: #333; font-size: 16px; margin-bottom: 8px;">1. Central Ducted HVAC System</h3>
            <p style="margin: 0; padding: 10px; background: ${formData.hasCentralHVAC ? '#e8f5e8' : '#fff3e0'}; border-radius: 5px; border-left: 4px solid ${formData.hasCentralHVAC ? '#4caf50' : '#ff9800'};">
              <strong>${formData.hasCentralHVAC ? 'YES' : 'NO'} - ${formData.hasCentralHVAC ? 'Customer has central ducted HVAC' : 'Customer does NOT have central ducted HVAC'}</strong>
            </p>
          </div>

          <div style="margin-bottom: 20px;">
            <h3 style="color: #333; font-size: 16px; margin-bottom: 8px;">2. Indoor Air Quality Interests</h3>
            <p style="margin: 0; padding: 15px; background: #f5f5f5; border-radius: 5px; border-left: 4px solid #00695C;">
              ${formData.iaqInterests || 'No specific interests provided'}
            </p>
          </div>

          <div style="margin-bottom: 20px;">
            <h3 style="color: #333; font-size: 16px; margin-bottom: 8px;">3. Existing Air Quality Equipment</h3>
            <p style="margin: 0 0 10px 0; padding: 10px; background: ${formData.hasExistingEquipment ? '#e8f5e8' : '#fff3e0'}; border-radius: 5px; border-left: 4px solid ${formData.hasExistingEquipment ? '#4caf50' : '#ff9800'};">
              <strong>${formData.hasExistingEquipment ? 'YES' : 'NO'} - ${formData.hasExistingEquipment ? 'Customer has existing equipment' : 'Customer does NOT have existing equipment'}</strong>
            </p>
            ${formData.existingEquipmentDetails ? `
              <p style="margin: 0; padding: 15px; background: #f5f5f5; border-radius: 5px; border-left: 4px solid #00695C;">
                <strong>Equipment Details:</strong><br>
                ${formData.existingEquipmentDetails}
              </p>
            ` : ''}
          </div>

          <div style="margin-bottom: 20px;">
            <h3 style="color: #333; font-size: 16px; margin-bottom: 8px;">4. Preferred HVAC Contractor</h3>
            <p style="margin: 0 0 10px 0; padding: 10px; background: ${formData.hasPreferredContractor ? '#e8f5e8' : '#fff3e0'}; border-radius: 5px; border-left: 4px solid ${formData.hasPreferredContractor ? '#4caf50' : '#ff9800'};">
              <strong>${formData.hasPreferredContractor ? 'YES' : 'NO'} - ${formData.hasPreferredContractor ? 'Customer has a preferred contractor' : 'Customer needs contractor referral'}</strong>
            </p>
            ${formData.contractorContact ? `
              <p style="margin: 0; padding: 15px; background: #f5f5f5; border-radius: 5px; border-left: 4px solid #00695C;">
                <strong>Contractor Contact:</strong><br>
                ${formData.contractorContact}
              </p>
            ` : ''}
          </div>
        </div>

        <div style="background: #00695C; padding: 20px; border-radius: 8px; color: white; text-align: center;">
          <h3 style="margin: 0 0 10px 0; font-size: 18px;">Next Steps</h3>
          <p style="margin: 0; font-size: 14px; opacity: 0.9;">
            • Customer expects contact within 1 week<br>
            • Review installation requirements and equipment compatibility<br>
            • ${formData.hasPreferredContractor ? 'Coordinate with preferred contractor' : 'Provide contractor referrals in customer area'}<br>
            • Schedule professional installation appointment
          </p>
        </div>

        <div style="margin-top: 30px; padding: 15px; background: #f8f9fa; border-radius: 5px; text-align: center; font-size: 12px; color: #666;">
          <p style="margin: 0;">This email was automatically generated from the HAVEN Store installation questionnaire.</p>
          <p style="margin: 5px 0 0 0;">Customer submitted this information immediately after purchase to ensure proper installation planning.</p>
        </div>
      </div>
    `;
  }

  private fallbackToMailto(formData: InstallationFormData): void {
    const subject = encodeURIComponent(`Professional Installation Details - Order ${formData.orderNumber || 'N/A'}`);
    const body = encodeURIComponent(`
Customer: ${formData.customerName} (${formData.customerEmail})
Product: ${formData.productName}
Order: ${formData.orderNumber || 'N/A'}

INSTALLATION QUESTIONNAIRE RESPONSES:

1. Central HVAC System: ${formData.hasCentralHVAC ? 'YES' : 'NO'}

2. IAQ Interests: ${formData.iaqInterests}

3. Existing Equipment: ${formData.hasExistingEquipment ? 'YES' : 'NO'}
${formData.existingEquipmentDetails ? `   Details: ${formData.existingEquipmentDetails}` : ''}

4. Preferred Contractor: ${formData.hasPreferredContractor ? 'YES' : 'NO'}
${formData.contractorContact ? `   Contact: ${formData.contractorContact}` : ''}

Please contact customer within 1 week to coordinate installation.
    `);

    const mailtoUrl = `mailto:support@haveniaq.com?subject=${subject}&body=${body}`;
    window.open(mailtoUrl);
  }
}

export const emailService = new EmailService(); 